/** Twilio's public lifecycle only; no private socket state or infinite retries. */
// A window can report hidden and visible over and over: Windows does so for a
// covered window. Each return used to restart the retries from zero, so a failing
// phone provider was asked every few seconds for as long as the page stayed open.
export const RESUME_AFTER_FAILURE_MS = 60000;

export const buildVoiceIdentity = (userId) => {
  let suffix;
  try {
    suffix = sessionStorage.getItem('voiceSessionId');
    if (!suffix) {
      suffix = window.crypto.randomUUID().replace(/-/g, '').slice(0, 16);
      sessionStorage.setItem('voiceSessionId', suffix);
    }
  } catch (_) {
    suffix = window.crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  }
  return `${userId}__${suffix}`;
};

export const createVoiceReceiver = ({
  identity, userId, getToken, heartbeat, leave, onIncoming, onStatus,
  getDeviceClass = () => window.Twilio?.Device,
  browser = window, page = document, timers = window,
}) => {
  let device = null;
  let stopped = false;
  let activeCall = null;
  let connecting = false;
  let refreshing = false;
  let retryTimer = null;
  let attempts = 0;
  let generation = 0;
  let lastFailureAt = 0;
  const retryDelays = [500, 1000, 2000, 4000, 8000];
  const emit = (state, reason = '') => { if (!stopped) onStatus({ state, reason }); };
  const online = () => browser.navigator.onLine !== false;
  const drop = () => {
    generation += 1;
    const previous = device;
    device = null;
    if (previous) { try { previous.destroy(); } catch (_) { /* already closed */ } }
    return previous || stopped ? Promise.resolve(leave(identity)).catch(() => {}) : Promise.resolve();
  };
  const fail = (error) => {
    if (stopped) return;
    lastFailureAt = Date.now();
    const forbidden = [401, 403].includes(error?.response?.status);
    if (forbidden) {
      emit('error', 'Anmeldung oder Anrufberechtigung bitte prüfen.');
      if (!activeCall) drop();
      return;
    }
    if (!online()) { emit('offline', 'Keine Netzwerkverbindung.'); return; }
    if (retryTimer) return;
    if (activeCall) { emit('reconnecting', 'Gesprächsverbindung wird geprüft.'); return; }
    if (attempts >= retryDelays.length) {
      emit('error', 'Verbindung fehlgeschlagen. Bitte erneut verbinden.');
      return;
    }
    emit('reconnecting', 'Verbindung wird wiederhergestellt.');
    retryTimer = timers.setTimeout(() => {
      retryTimer = null;
      connect();
    }, retryDelays[attempts++]);
  };
  const refresh = async () => {
    if (stopped || !device || refreshing || connecting) return;
    refreshing = true;
    const current = device;
    try {
      const data = await getToken(userId, identity);
      if (stopped || device !== current) return;
      current.updateToken(data.token);
      if (current.state === 'unregistered' && !activeCall) await current.register();
      await heartbeat(identity);
      if (!stopped && current === device) emit('ready');
    } catch (error) { fail(error); }
    finally { refreshing = false; }
  };
  const connect = async () => {
    if (stopped || connecting || activeCall) return;
    if (!online()) { emit('offline', 'Keine Netzwerkverbindung.'); return; }
    connecting = true;
    await drop();
    if (stopped) { connecting = false; return; }
    const currentGeneration = generation;
    emit(attempts ? 'reconnecting' : 'connecting');
    try {
      const Device = getDeviceClass();
      if (!Device) throw new Error('sdk-unavailable');
      const data = await getToken(userId, identity);
      if (stopped || generation !== currentGeneration) {
        if (stopped) Promise.resolve(leave(identity)).catch(() => {});
        return;
      }
      const current = new Device(data.token, {
        logLevel: 'warn', codecPreferences: ['opus', 'pcmu'], tokenRefreshMs: 60000,
      });
      device = current;
      current.on('incoming', (call) => {
        if (stopped || current !== device) { call.reject(); return; }
        if (activeCall) { call.reject(); return; }
        activeCall = call;
        const ended = () => {
          if (activeCall === call) activeCall = null;
          if (current === device && current.state !== 'registered') fail();
        };
        ['cancel', 'disconnect', 'error', 'reject'].forEach((event) => call.on(event, ended));
        onIncoming(call);
      });
      current.on('registered', () => {
        if (current === device && !connecting) emit('ready');
      });
      current.on('unregistered', () => { if (current === device) fail(); });
      current.on('tokenWillExpire', refresh);
      current.on('error', (error) => { if (current === device) fail(error); });
      await current.register();
      if (stopped || current !== device) return;
      await heartbeat(identity);
      attempts = 0;
      lastFailureAt = 0;
      emit('ready');
    } catch (error) {
      if (!stopped && generation === currentGeneration) fail(error);
    } finally { connecting = false; }
  };
  const resume = () => {
    if (stopped || page.visibilityState === 'hidden') return;
    attempts = 0;
    if (retryTimer) timers.clearTimeout(retryTimer);
    retryTimer = null;
    if (device && (device.state === 'registered' || activeCall)) refresh();
    else connect();
  };
  // Only returning to the window waits. Coming back online and the reconnect
  // button are real reasons to try again straight away.
  const visibleAgain = () => {
    if (!activeCall && lastFailureAt && Date.now() - lastFailureAt < RESUME_AFTER_FAILURE_MS) return;
    resume();
  };
  const offline = () => emit('offline', 'Keine Netzwerkverbindung.');
  const interval = timers.setInterval(() => {
    if (device?.state === 'registered' && online()) {
      Promise.resolve(heartbeat(identity)).catch(fail);
    }
  }, 120000);
  const refreshInterval = timers.setInterval(refresh, 45 * 60 * 1000);
  browser.addEventListener('online', resume);
  browser.addEventListener('offline', offline);
  page.addEventListener('visibilitychange', visibleAgain);
  connect();
  return {
    reconnect: resume,
    // The call UI may select outputs through Twilio's public AudioHelper.
    getAudio: () => device?.audio || null,
    stop: () => {
      stopped = true;
      if (retryTimer) timers.clearTimeout(retryTimer);
      timers.clearInterval(interval);
      timers.clearInterval(refreshInterval);
      browser.removeEventListener('online', resume);
      browser.removeEventListener('offline', offline);
      page.removeEventListener('visibilitychange', visibleAgain);
      drop();
    },
  };
};
